import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

interface TechType {
  name: string;
  energy: string;
  capacity: string;
  color: string;
}

interface Application {
  name: string;
  dailyOutput: string;
  color: string;
}

interface MajorPlant {
  name: string;
  location: string;
  capacity: string;
  color: string;
}

interface EnergyBar {
  tech: string;
  kwh: number;
  color: string;
}

interface DesalinationTemplateProps {
  topic: string;
  subtitle: string;
  technologies: TechType[];
  applications: Application[];
  processSteps: string[];
  majorPlants: MajorPlant[];
  energyBars: EnergyBar[];
  stats: { value: string; label: string }[];
  bottomInsight: string;
}

const DEFAULT_PROPS: DesalinationTemplateProps = {
  topic: '海水淡化与水资源',
  subtitle: 'Desalination & Water Resources',
  technologies: [
    { name: '反渗透 RO', energy: '3-4 kWh/m³', capacity: '日产百万m³级', color: '#06b6d4' },
    { name: '多级闪蒸 MSF', energy: '10-16 kWh/m³', capacity: '日产数十万m³', color: '#1e3a5f' },
    { name: '电渗析 ED', energy: '1-2 kWh/m³', capacity: '苦咸水处理为主', color: '#22c55e' },
    { name: '太阳能蒸馏', energy: '太阳能驱动', capacity: '小型分散供水', color: '#f59e0b' },
  ],
  applications: [
    { name: '城市供水', dailyOutput: '50万m³/日', color: '#06b6d4' },
    { name: '工业用水', dailyOutput: '20万m³/日', color: '#94a3b8' },
    { name: '农业灌溉', dailyOutput: '10万m³/日', color: '#22c55e' },
    { name: '船舶淡水', dailyOutput: '500m³/日', color: '#1e3a5f' },
  ],
  processSteps: ['取水', '预处理', '高压泵', '膜组件', '后处理', '输配'],
  majorPlants: [
    { name: 'Ras Al Khair', location: '沙特阿拉伯', capacity: '日产104万m³', color: '#f59e0b' },
    { name: 'Sorek', location: '以色列', capacity: '日产62万m³', color: '#06b6d4' },
    { name: 'Tuas', location: '新加坡', capacity: '日产30万m³', color: '#22c55e' },
    { name: '百发海水淡化', location: '中国青岛', capacity: '日产20万m³', color: '#ef4444' },
  ],
  energyBars: [
    { tech: 'RO', kwh: 3.5, color: '#06b6d4' },
    { tech: 'MSF', kwh: 13, color: '#1e3a5f' },
    { tech: 'ED', kwh: 1.5, color: '#22c55e' },
    { tech: '太阳能', kwh: 0.5, color: '#f59e0b' },
  ],
  stats: [
    { value: '20000+', label: '全球淡化厂数量' },
    { value: '1亿m³/日', label: '全球日产淡水量' },
    { value: '3-4kWh/m³', label: 'RO 能耗' },
  ],
  bottomInsight: '海水淡化让人类摆脱淡水资源的地理束缚——科技是解渴之源',
};

export const DesalinationTemplate: React.FC<Partial<DesalinationTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const headerIn = spring({ frame, fps, config: { damping: 18, stiffness: 80 } });

  const techStart = 50;
  const membraneDrawStart = 20;
  const appStart = 50;
  const processStart = 200;
  const envStart = 280;
  const plantStart = 320;
  const statStart = 350;

  const membraneDraw = interpolate(frame, [membraneDrawStart, membraneDrawStart + 80], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  const waterMoleculePhase = interpolate(frame, [40, 300], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(135deg, #0a141e 0%, #0f1a28 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Noto Sans SC", sans-serif',
        color: 'white',
        overflow: 'hidden',
        width: 1920,
        height: 1080,
      }}
    >
      {/* Subtle wave pattern */}
      <svg style={{ position: 'absolute', top: 0, left: 0, width: 1920, height: 1080, pointerEvents: 'none' }}>
        {[0, 1, 2].map((wi) => {
          const yBase = 300 + wi * 300;
          const waveOffset = frame * 0.4 + wi * 40;
          const d = `M0,${yBase} ${Array.from({ length: 20 })
            .map((_, j) => `Q${j * 100 + 50},${yBase + Math.sin((j + waveOffset * 0.02) * 0.8) * 12} ${(j + 1) * 100},${yBase}`)
            .join(' ')}`;
          return <path key={wi} d={d} fill="none" stroke="#1e3a5f" strokeWidth="1" opacity="0.12" />;
        })}
      </svg>

      {/* Header */}
      <div
        style={{
          position: 'absolute',
          top: 28,
          left: 44,
          opacity: headerIn,
          transform: `translateY(${(1 - headerIn) * 16}px)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div
            style={{
              padding: '5px 16px',
              background: '#0c2d48',
              borderRadius: 8,
              fontSize: 22,
              fontWeight: 700,
              color: '#06b6d4',
              border: '1px solid #06b6d430',
              whiteSpace: 'nowrap',
            }}
          >
            💧 水资源科技
          </div>
          <h1 style={{ fontSize: 40, fontWeight: 900, margin: 0, whiteSpace: 'nowrap' }}>{p.topic}</h1>
          <p style={{ fontSize: 26, color: '#64748b', margin: 0, whiteSpace: 'nowrap' }}>{p.subtitle}</p>
        </div>
      </div>

      {/* ===== LEFT PANEL: Technology Types ===== */}
      <div style={{ position: 'absolute', top: 120, left: 44, width: 350 }}>
        <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.1em', fontWeight: 600 }}>
          淡化技术
        </div>
        {p.technologies.map((t, i) => {
          const s = spring({
            frame: frame - (techStart + i * 18),
            fps,
            config: { damping: 14, stiffness: 120 },
          });
          return (
            <div
              key={i}
              style={{
                background: '#0c1a2a',
                borderRadius: 12,
                padding: '10px 14px',
                marginBottom: 8,
                border: `1px solid ${t.color}30`,
                opacity: s,
                transform: `translateX(${(1 - s) * -30}px)`,
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 800, color: t.color }}>{t.name}</div>
              <div style={{ display: 'flex', gap: 14, marginTop: 4 }}>
                <div style={{ fontSize: 22, color: '#64748b' }}>
                  能耗：<span style={{ color: '#94a3b8', fontWeight: 600 }}>{t.energy}</span>
                </div>
              </div>
              <div style={{ fontSize: 22, color: '#64748b', marginTop: 2 }}>
                产能：<span style={{ color: '#94a3b8', fontWeight: 600 }}>{t.capacity}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* ===== CENTER: RO Membrane SVG ===== */}
      <svg
        style={{ position: 'absolute', left: 480, top: 135 }}
        width="560"
        height="380"
        viewBox="0 0 560 380"
      >
        <defs>
          <linearGradient id="saltWaterGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1e3a5f" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#0a141e" stopOpacity="0.3" />
          </linearGradient>
          <linearGradient id="freshWaterGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#0a141e" stopOpacity="0.2" />
          </linearGradient>
          <clipPath id="membraneClip">
            <rect x="0" y="0" width="560" height="380" />
          </clipPath>
        </defs>

        <rect
          x="20" y="30" width="210" height="300" rx="8"
          fill="url(#saltWaterGrad)"
          stroke="#1e3a5f"
          strokeWidth="2"
          opacity={membraneDraw}
        />
        <text x="125" y="20" fill="#1e3a5f" fontSize="24" textAnchor="middle" fontWeight="700"
          opacity={membraneDraw}>
          高压盐水侧
        </text>

        <rect
          x="240" y="20" width="80" height="320" rx="6"
          fill="#94a3b820"
          stroke="#94a3b8"
          strokeWidth="2"
          strokeDasharray="6 3"
          opacity={membraneDraw}
        />
        {Array.from({ length: 15 }).map((_, i) => (
          <line
            key={`mp-${i}`}
            x1="250"
            y1={35 + i * 20}
            x2="310"
            y2={35 + i * 20}
            stroke="#94a3b8"
            strokeWidth="0.8"
            opacity={membraneDraw * 0.4}
          />
        ))}
        <text x="280" y="370" fill="#94a3b8" fontSize="24" textAnchor="middle" fontWeight="700"
          opacity={membraneDraw}>
          半透膜
        </text>

        <rect
          x="330" y="30" width="210" height="300" rx="8"
          fill="url(#freshWaterGrad)"
          stroke="#06b6d4"
          strokeWidth="2"
          opacity={membraneDraw}
        />
        <text x="435" y="20" fill="#06b6d4" fontSize="24" textAnchor="middle" fontWeight="700"
          opacity={membraneDraw}>
          淡水侧
        </text>

        <g opacity={interpolate(frame, [50, 80], [0, 0.9], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
          <line x1="15" y1="180" x2="40" y2="180" stroke="#ef4444" strokeWidth="3" markerEnd="url(#pressArrow)" />
          <text x="5" y="220" fill="#ef4444" fontSize="24" fontWeight="700">高压</text>
        </g>
        <defs>
          <marker id="pressArrow" markerWidth="8" markerHeight="6" refX="8" refY="3" orient="auto">
            <polygon points="0,0 8,3 0,6" fill="#ef4444" />
          </marker>
        </defs>

        <g clipPath="url(#membraneClip)">
          {Array.from({ length: 10 }).map((_, i) => {
            const yPos = 50 + i * 28;
            const xStart = 100 + (i % 3) * 30;
            const passProgress = interpolate(
              waterMoleculePhase,
              [i * 0.08, i * 0.08 + 0.4],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            const xPos = interpolate(passProgress, [0, 0.45, 0.55, 1], [xStart, 240, 320, 420 + (i % 3) * 20], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            const squeeze = passProgress > 0.4 && passProgress < 0.6
              ? interpolate(passProgress, [0.4, 0.5, 0.6], [1, 0.6, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })
              : 1;
            return (
              <circle
                key={`water-${i}`}
                cx={xPos}
                cy={yPos}
                r={5 * squeeze}
                fill="#06b6d4"
                opacity={interpolate(passProgress, [0, 0.05], [0, 0.85], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}
              />
            );
          })}
        </g>

        <g clipPath="url(#membraneClip)">
          {Array.from({ length: 6 }).map((_, i) => {
            const yPos = 60 + i * 45;
            const xStart = 110 + (i % 2) * 40;
            const blockProgress = interpolate(
              waterMoleculePhase,
              [i * 0.1, i * 0.1 + 0.3],
              [0, 1],
              { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
            );
            const xPos = interpolate(blockProgress, [0, 0.5, 1], [xStart, 230, xStart + 20], {
              extrapolateLeft: 'clamp',
              extrapolateRight: 'clamp',
            });
            return (
              <g key={`salt-${i}`} opacity={interpolate(blockProgress, [0, 0.05], [0, 0.8], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
                <circle cx={xPos} cy={yPos} r={4} fill="#ef4444" />
                <text x={xPos} y={yPos + 3} fill="#ef4444" fontSize="6" textAnchor="middle" fontWeight="700">Na⁺</text>
              </g>
            );
          })}
        </g>
      </svg>

      <div style={{
        position: 'absolute',
        left: 385,
        top: 520,
        width: 746,
        fontSize: 24,
        color: '#64748b',
        opacity: membraneDraw,
        textAlign: 'center',
      }}>
        ▲ 反渗透膜截面 · 蓝色水分子通过 · 红色盐离子被拦截
      </div>

      {/* Applications moved to bottom row */}

      {/* ===== Water Stress Map ===== */}
      <svg
        style={{ position: 'absolute', left: 1100, top: 100 }}
        width="780"
        height="310"
        viewBox="0 0 520 220"
      >
        <text x="260" y="40" fill="#64748b" fontSize="18" textAnchor="middle" fontWeight="600"
          opacity={interpolate(frame, [120, 150], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
          全球水资源压力分布（简化）
        </text>
        {[
          { x: 20, y: 63, w: 80, h: 50, label: '北美', stress: false },
          { x: 115, y: 75, w: 50, h: 60, label: '南美', stress: false },
          { x: 180, y: 57, w: 70, h: 45, label: '欧洲', stress: false },
          { x: 180, y: 107, w: 80, h: 50, label: '非洲', stress: true },
          { x: 275, y: 57, w: 120, h: 55, label: '亚洲', stress: true },
          { x: 275, y: 120, w: 50, h: 30, label: '印度', stress: true },
          { x: 410, y: 95, w: 70, h: 50, label: '澳洲', stress: true },
        ].map((cont, i) => {
          const mapOpacity = interpolate(frame, [130 + i * 10, 150 + i * 10], [0, 1], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          });
          return (
            <g key={i} opacity={mapOpacity}>
              <rect
                x={cont.x} y={cont.y} width={cont.w} height={cont.h} rx="6"
                fill={cont.stress ? '#ef444430' : '#06b6d415'}
                stroke={cont.stress ? '#ef4444' : '#06b6d4'}
                strokeWidth="1.2"
              />
              <text
                x={cont.x + cont.w / 2} y={cont.y + cont.h / 2 + 5}
                fill={cont.stress ? '#ef4444' : '#06b6d4'}
                fontSize="15" textAnchor="middle" fontWeight="700"
              >
                {cont.label}
              </text>
            </g>
          );
        })}
        <g opacity={interpolate(frame, [210, 240], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' })}>
          <rect x="440" y="159" width="14" height="14" rx="2" fill="#ef444430" stroke="#ef4444" strokeWidth="1" />
          <text x="459" y="172" fill="#64748b" fontSize="16">水资源紧张</text>
          <rect x="440" y="180" width="14" height="14" rx="2" fill="#06b6d415" stroke="#06b6d4" strokeWidth="1" />
          <text x="459" y="193" fill="#64748b" fontSize="16">相对充裕</text>
        </g>
      </svg>

      {/* ===== Process Flow ===== */}
      <div style={{ position: 'absolute', top: 430, left: 1100, width: 780 }}>
        <div style={{ fontSize: 25, color: '#64748b', marginBottom: 14, letterSpacing: '0.1em', fontWeight: 600 }}>
          反渗透工艺流程
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0 }}>
          {p.processSteps.map((step, i) => {
            const s = spring({
              frame: frame - (processStart + i * 16),
              fps,
              config: { damping: 14, stiffness: 120 },
            });
            const nodeColors = ['#1e3a5f', '#94a3b8', '#22c55e', '#06b6d4', '#94a3b8', '#06b6d4'];
            const isLast = i === p.processSteps.length - 1;
            return (
              <React.Fragment key={i}>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    opacity: s,
                    transform: `scale(${s})`,
                  }}
                >
                  <div
                    style={{
                      width: 58,
                      height: 58,
                      borderRadius: 14,
                      background: '#0c1a2a',
                      border: `2px solid ${nodeColors[i]}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 24,
                      fontWeight: 800,
                      color: nodeColors[i],
                    }}
                  >
                    {i + 1}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#e2e8f0', marginTop: 6 }}>{step}</div>
                </div>
                {!isLast && (
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 26 }}>
                    <svg width="56" height="18" viewBox="0 0 56 18">
                      <line x1="0" y1="9" x2="44" y2="9" stroke="#06b6d4" strokeWidth="2.5"
                        opacity={interpolate(frame, [processStart + i * 16 + 8, processStart + i * 16 + 18], [0, 0.6], {
                          extrapolateLeft: 'clamp',
                          extrapolateRight: 'clamp',
                        })} />
                      <polygon points="44,3 56,9 44,15" fill="#06b6d4"
                        opacity={interpolate(frame, [processStart + i * 16 + 8, processStart + i * 16 + 18], [0, 0.6], {
                          extrapolateLeft: 'clamp',
                          extrapolateRight: 'clamp',
                        })} />
                    </svg>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ===== Bottom Row: Environmental + Plants + Applications ===== */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 44,
          right: 44,
          display: 'flex',
          gap: 20,
          alignItems: 'flex-start',
        }}
      >
        {/* Environmental (vertical layout) */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.1em', fontWeight: 600 }}>
            环境影响与绿色方案
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[
              { title: '排放', desc: '盐度为海水2倍，影响近岸生态', icon: '⚠️', color: '#ef4444' },
              { title: '可再生能源', desc: '太阳能/风能驱动淡化，减碳60%+', icon: '♻️', color: '#22c55e' },
            ].map((env, i) => {
              const s = spring({
                frame: frame - (envStart + i * 20),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0c1a2a',
                    borderRadius: 12,
                    padding: '10px 14px',
                    border: `1px solid ${env.color}30`,
                    opacity: s,
                    transform: `translateY(${(1 - s) * 16}px)`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                    <span style={{ fontSize: 22 }}>{env.icon}</span>
                    <div style={{ fontSize: 22, fontWeight: 800, color: env.color }}>{env.title}</div>
                  </div>
                  <div style={{ fontSize: 22, color: '#94a3b8', lineHeight: 1.5 }}>{env.desc}</div>
                </div>
              );
            })}
            <div
              style={{
                background: '#0c1a2a',
                borderRadius: 12,
                padding: '10px 14px',
                border: '1px solid #06b6d430',
                opacity: spring({ frame: frame - (envStart + 40), fps, config: { damping: 14, stiffness: 100 } }),
              }}
            >
              <div style={{ fontSize: 22, fontWeight: 900, color: '#06b6d4' }}>1亿m³/日</div>
              <div style={{ fontSize: 22, color: '#64748b', marginTop: 2 }}>全球日产淡水量</div>
            </div>
          </div>
        </div>

        {/* Major Plants */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.1em', fontWeight: 600 }}>
            世界大型淡化厂
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {p.majorPlants.map((pl, i) => {
              const s = spring({
                frame: frame - (plantStart + i * 16),
                fps,
                config: { damping: 14, stiffness: 110 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0c1a2a',
                    borderRadius: 12,
                    padding: '8px 12px',
                    borderLeft: `3px solid ${pl.color}`,
                    opacity: s,
                    transform: `translateX(${(1 - s) * 20}px)`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#f8fafc' }}>{pl.name}</div>
                    <div style={{ fontSize: 22, color: '#64748b' }}>{pl.location}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: pl.color }}>{pl.capacity}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Applications */}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.1em', fontWeight: 600 }}>
            应用领域
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {p.applications.map((a, i) => {
              const s = spring({
                frame: frame - (appStart + i * 18),
                fps,
                config: { damping: 14, stiffness: 120 },
              });
              return (
                <div
                  key={i}
                  style={{
                    background: '#0c1a2a',
                    borderRadius: 10,
                    padding: '10px 16px',
                    border: `1px solid ${a.color}40`,
                    opacity: s,
                    transform: `translateX(${(1 - s) * 30}px)`,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ fontSize: 22, fontWeight: 700, color: a.color }}>{a.name}</div>
                    <div style={{ fontSize: 20, color: '#94a3b8' }}>{a.dailyOutput}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Energy Comparison */}
        <div
          style={{
            flex: 1,
            opacity: spring({ frame: frame - statStart, fps, config: { damping: 14, stiffness: 100 } }),
            transform: `translateY(${(1 - spring({ frame: frame - statStart, fps, config: { damping: 14, stiffness: 100 } })) * 16}px)`,
          }}
        >
          <div style={{ fontSize: 26, color: '#64748b', marginBottom: 10, letterSpacing: '0.1em', fontWeight: 600 }}>
            能耗对比 (kWh/m³)
          </div>
          <div
            style={{
              background: '#0c1a2a',
              borderRadius: 12,
              padding: '16px 18px',
              border: '1px solid #1e293b',
            }}
          >
            <div style={{ fontSize: 24, fontWeight: 900, color: '#06b6d4' }}>20000+</div>
            <div style={{ fontSize: 22, color: '#64748b', marginTop: 4 }}>全球淡化厂数量</div>
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, 540], [0, 100], {
            extrapolateLeft: 'clamp',
            extrapolateRight: 'clamp',
          })}%`,
          height: 3,
          background: 'linear-gradient(90deg, #1e3a5f, #06b6d4, #22c55e)',
        }}
      />
    </AbsoluteFill>
  );
};
